import {
  ChangeDetectionStrategy,
  Component,
  ElementRef, HostListener,
  Inject,
  Input, OnDestroy,
  Optional,
  TemplateRef,
  ViewChild
} from "@angular/core";
import {IntPortalOutletService} from "../../../modules/portal-outlet/int-portal-outlet.service";
import {WINDOW_REF_TOKEN} from "../../../tokens";
import {BehaviorSubject, fromEvent, merge, Observable} from "rxjs";
import {KEYBOARD_KEY_PRESSED} from "../../../providers/keyboardEvents.provider";
import {DestroyStreamService} from "../../../services";
import {takeUntil} from "rxjs/operators";
import {DROPDOWN_ANIMATION} from "../../../animations";

interface IDropCoords {
top: string, left?: string, right?: string
}

@Component({
  selector: 'int-wrap-dropdown',
  templateUrl: './int-wrap-dropdown.component.html',
  styleUrls: ['./int-wrap-dropdown.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    DestroyStreamService
  ],
  animations: [DROPDOWN_ANIMATION]
})
export class IntWrapDropdownComponent<T> implements OnDestroy{
  @Input() content!: TemplateRef<T>;
  @Input() dropAreaWidth = 200;
  @Input() horizontalOffset = 0;
  @Input() verticalOffset = 0;
  @Input() align = 'left';

  @ViewChild('wrap') readonly wrap!: ElementRef<HTMLBaseElement>;
  @ViewChild('dropArea') readonly dropArea!: TemplateRef<HTMLElement>;

  id = `${Math.random()}`;
  isOpen = false;

  position$ = new BehaviorSubject<IDropCoords>({top: '', left: '', right: ''});

  animation$ = new BehaviorSubject<boolean>(false);

  @HostListener('document:click', ['$event']) documentClick(event: Event) {

    // @ts-ignore
    if(!this.wrap?.nativeElement.contains(event.target)) {
      this.close();
    }
  }

  constructor(
    @Optional()
    private readonly portal: IntPortalOutletService,
    @Inject(WINDOW_REF_TOKEN) private readonly windowRef: Window,
    @Inject(KEYBOARD_KEY_PRESSED) private readonly keyService: Observable<string>,
    private readonly destroy$: DestroyStreamService
  ) {

    this.keyService.pipe(takeUntil(this.destroy$)).subscribe((key) => {
      if(key === 'Escape') {
        this.close();
      }
    });

    merge(
      fromEvent(this.windowRef, 'scroll')
    ).pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.close();
    });

  }

  ngOnDestroy() {
    this.close();
  }

  getClientRect() {
    return this.wrap?.nativeElement?.getBoundingClientRect();
  }

  open() {
    const {top, left, right} = this.getClientRect();
    const windowWidth = this.windowRef.innerWidth;

    const coords: IDropCoords = {
      top: `${top + this.verticalOffset}px`
    }
    if(this.align === 'right') {
      coords.right = `${windowWidth - right + this.horizontalOffset}px`
    }
    if(this.align === 'left') {
      coords.left = `${left + this.horizontalOffset}px`
    }
    this.position$.next(coords);

    this.animation$.next(true);

    this.portal.setTemplateOut(this.dropArea, this.position$.value);
    this.isOpen = true;

  }

  close() {
    this.animation$.next(false);
    setTimeout(() => {
      this.portal.removeTemplateOut(this.dropArea);
      this.isOpen = false;
    }, 200);
  }

  onClick(event: Event) {
    event.stopPropagation();
  }

  onToggle() {
    if(this.isOpen) {
      this.close();

    }else {
      this.open();
    }
  }
}
